import NavBar from "frontend/Components/DesignSystem/NavBar";
import HeroAnimation from "frontend/Components/HeroAnimation/HeroAnimation";
import React, { useState, useEffect } from "react";
import "../../../css/Common/Pages/AboutUsPage/AboutUsPage.css";
import CardProduct from "frontend/Components/DesignSystem/Cards/CardProduct";
import { ReactComponent as NewsIcon } from "../../../assets/icons/news.svg";
import { ReactComponent as FinancialAdviceIcon } from "../../../assets/icons/free-financial-advice.svg";
import { ReactComponent as BlogIcon } from "../../../assets/icons/blog.svg";
import { ContactUs } from "frontend/Components/ContactForm/ContactUs";
import Footer from "frontend/Components/DesignSystem/Footer";
import { RequestCallback } from "frontend/Components/ContactForm/RequestCallback";
import SeoHelmet from "frontend/Components/SEO/SeoHelmet";
import { SEO_PAGES, SEO_SITE, getPageUrl } from "frontend/Utils/seoConfig";
import ImageDirectory from "frontend/HelperFunctions/ImageDirectory";
import { fetchNoOfAdvisers } from "frontend/Utils/GetNoOfAdvisers";

// AboutUsPage component displays the About Us page with company info, mission, and interactive cards
const AboutUsPage = () => {
  // Navigation items for the NavBar
  const navigationItems = [
    { label: "Home", navigateTo: "/" },
    { label: "Our Services", navigateTo: "/OurServices" },
    { label: "About Us", navigateTo: "/AboutUs" },
    { label: "Contact Us", navigateTo: "/ContactUs" },
  ];

  // State to control visibility of the callback (consultation) modal
  const [isCallbackVisible, setCallbackVisible] = useState(false);

  // State to store the number of advisers fetched from the backend
  const [adviserCount, setAdviserCount] = useState(null);

  // Fetch the number of advisers when the component mounts
  useEffect(() => {
    const getCount = async () => {
      const count = await fetchNoOfAdvisers();
      setAdviserCount(count);
    };
    getCount();
  }, []);

  
  /**
   * Handles the action to open the callback modal or component.
   * Sets the state to make the callback visible.
   *
   * @function
   * @returns {void}
   */
  const handleOpenCallback = () => {
    setCallbackVisible(true);
  };

 
  /**
   * Handles the close action for the callback component.
   * Sets the callback visibility state to false, effectively hiding the callback UI.
   *
   * @function
   * @returns {void}
   */
  const handleCloseCallback = () => {
    setCallbackVisible(false);
  };
  const aboutPageStructuredData = {
    "@type": "AboutPage",
    name: SEO_PAGES.customerAboutUs.title,
    description: SEO_PAGES.customerAboutUs.description,
    url: getPageUrl("/AboutUs"),
    about: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
    mainEntity: {
      "@id": `${SEO_SITE.siteUrl}/#organization`,
    },
  };

  const financialServiceStructuredData = {
    "@type": "FinancialService",
    "@id": `${SEO_SITE.siteUrl}/#financialservice`,
    name: SEO_SITE.siteName,
    url: SEO_SITE.siteUrl,
    description:
      "Rosemount Financial Solutions is a full financial planning firm with nationwide coverage, helping customers build bespoke plans for their financial goals.",
    areaServed: "United Kingdom",
    foundingDate: "2004",
    serviceType: [
      "Financial Planning",
      "Mortgage Advice",
      "Protection Advice",
      "Investment Planning",
      "Retirement Planning",
      "Independent Financial Advice",
    ],
  };

  // Render the About Us page
  return (
    <div className="AboutUs-Page">
      {/* Set page title and meta description */}
      <SeoHelmet
        page={SEO_PAGES.customerAboutUs}
        breadcrumbs={[
          {
            name: "Home",
            url: getPageUrl("/"),
          },
          {
            name: "About Us",
            url: getPageUrl("/AboutUs"),
          },
        ]}
        extraSchemas={[aboutPageStructuredData, financialServiceStructuredData]}
      />
      {/* Navigation bar */}
      <NavBar navigationItems={navigationItems} />
      {/* Hero section with animation and title */}
      <HeroAnimation
        imgSrc={ImageDirectory["customer-about-min"]}
        altText="An adviser holding paper work speaking with a couple on the sofa."
        children={
          <>
            <h1>Why Rosemount?</h1>
            <div className="SubTitle">
              Our advisers aim is to have a positive impact on your life
            </div>
          </>
        }
      />
      {/* Company information section */}
      <div className="AboutUs-Container">
        <h2>Who We Are?</h2>
        <div className="SubTitle">
          We are a full financial planning firm with nationwide coverage. Our
          advisers grow meaningful relationships to build bespoke plans for your
          goals.
        </div>
        <div className="SubTitle">
          Founded in 2004 we now have over {adviserCount} advisers and continue
          to expand each year. The company began as Mortgage Solutions for You,
          however we rebranded to Rosemount Financial Solutions (IFA) Ltd in
          2006.
        </div>
        <div className="SubTitle">
          Whilst the financial crash in 2008 affected our industry, we remained
          resilient and continued to support our clients through this period.
        </div>
      </div>
      {/* Mission statement section */}
      <div className="Services-Info-box1">
        <div className="Services-Info-box1-Content">
          <h2>Our Mission</h2>
          <div className="SubTitle">
            We are here to help you. Our financial advisers grow meaningful
            relationships to build trust and understanding for your financial
            aims
          </div>
        </div>
      </div>
      {/* Cards for News, Free Consultation, and Blog */}
      <div className="About-Us">
        <div className="Adviser-Services-Holder1">
          <CardProduct Icon={NewsIcon} title="News" link="/RosemountInMedia" />
          <div onClick={handleOpenCallback}>
            <CardProduct
              Icon={FinancialAdviceIcon}
              title="Free Initial Consultation"
            />
          </div>
          <CardProduct Icon={BlogIcon} title="Blog" link="/RosemountBlog" />
        </div>
      </div>
      {/* Contact form */}
      <ContactUs isAdviser={false} />
      {/* Footer */}
      <Footer />
      {/* Modal for free initial consultation callback */}
      {isCallbackVisible && (
        <div className="notification-overlay">
          <RequestCallback
            title="Free Initial Consultation"
            onClose={() => handleCloseCallback()}
          />
        </div>
      )}
    </div>
  );
};

export default AboutUsPage;
